import { StandardModal } from '../StandardModal';
import { Button, OutlineButton } from '../Button';
import { useSubscription, useSubscriptionTiers } from '../../hooks/useSubscription';
import { Crown, ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatTierLimit } from '../../utils/formatters';
import { APP_ROUTES } from '../../config/routes';

interface UpgradePromptProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when modal is closed
   */
  onClose: () => void;

  /**
   * Resource that triggered the upgrade prompt
   */
  resource?: 'workflows' | 'hosts' | 'users' | 'storage';

  /**
   * Custom message
   */
  message?: string;

  /**
   * Suggested tier to upgrade to (tier slug)
   */
  suggestedTier?: string;
}

const resourceMessages = {
  workflows: "You've reached your workflow limit.",
  hosts: "You've reached your host limit.",
  users: "You've reached your team member limit.",
  storage: "You've reached your storage limit.",
};

/**
 * Modal component prompting users to upgrade their subscription
 */
export default function UpgradePrompt({
  isOpen,
  onClose,
  resource,
  message,
  suggestedTier,
}: UpgradePromptProps) {
  const { data: currentTier } = useSubscription();
  const { data: tiers } = useSubscriptionTiers();

  const defaultMessage = resource
    ? resourceMessages[resource]
    : 'Upgrade your plan to access this feature.';

  const displayMessage = message || defaultMessage;

  // Find the suggested tier, or default to the next tier up from current
  const upgradeTier = (() => {
    if (suggestedTier) {
      return tiers?.find((t) => t.slug === suggestedTier);
    }
    // Auto-suggest the next tier if not specified
    if (currentTier && tiers) {
      const currentIndex = tiers.findIndex((t) => t.id === currentTier.id);
      if (currentIndex >= 0 && currentIndex < tiers.length - 1) {
        return tiers[currentIndex + 1];
      }
    }
    return undefined;
  })();

  return (
    <StandardModal
      open={isOpen}
      onClose={onClose}
      title="Upgrade Your Plan"
      size="md"
      footer={
        <>
          <OutlineButton size="sm" onClick={onClose}>
            Not Now
          </OutlineButton>
          <Link to={APP_ROUTES.SETTINGS_BILLING}>
            <Button size="sm" variant="primary" onClick={onClose}>
              View Plans <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </>
      }
    >
      <div className="space-y-4">
        {/* Icon and message */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
            <Crown className="h-8 w-8 text-amber-700 dark:text-amber-400" />
          </div>
          <p className="text-lg text-zinc-700 dark:text-zinc-300 mb-2">{displayMessage}</p>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Upgrade to {upgradeTier?.name || 'a higher tier'} to unlock more capacity and features.
          </p>
        </div>

        {/* Current tier */}
        {currentTier && (
          <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                Current Plan
              </span>
              <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {currentTier.name}
              </span>
            </div>
            {resource && (
              <div className="text-xs text-zinc-500 dark:text-zinc-500">
                {resource === 'workflows' &&
                  `${formatTierLimit(currentTier.maxWorkflows)} workflows`}
                {resource === 'hosts' && `${formatTierLimit(currentTier.maxHosts)} hosts`}
                {resource === 'users' && `${formatTierLimit(currentTier.maxUsers)} team members`}
              </div>
            )}
          </div>
        )}

        {/* Suggested upgrade */}
        {upgradeTier && (
          <div className="bg-sky-50 dark:bg-sky-900/20 rounded-lg p-4 border-2 border-sky-300 dark:border-sky-700">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Upgrade to
              </span>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
                  {upgradeTier.name}
                </span>
                <span className="text-sm text-zinc-600 dark:text-zinc-400">
                  {(() => {
                    if (upgradeTier.priceMonthlyCents > 0) {
                      return `$${(upgradeTier.priceMonthlyCents / 100).toFixed(0)}/mo`;
                    }
                    if (upgradeTier.priceYearlyCents > 0) {
                      return `$${(upgradeTier.priceYearlyCents / 100 / 12).toFixed(0)}/mo`;
                    }
                    return 'Custom pricing';
                  })()}
                </span>
              </div>
            </div>

            {/* Key benefits */}
            <div className="space-y-1.5 mt-3">
              {resource && (
                <div className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-700 dark:text-green-400 mt-0.5 shrink-0" />
                  <span className="text-zinc-700 dark:text-zinc-300">
                    {resource === 'workflows' &&
                      `${formatTierLimit(upgradeTier.maxWorkflows)} workflows`}
                    {resource === 'hosts' && `${formatTierLimit(upgradeTier.maxHosts)} hosts`}
                    {resource === 'users' &&
                      `${formatTierLimit(upgradeTier.maxUsers)} team members`}
                  </span>
                </div>
              )}
              {(upgradeTier.features?.['has_realtime_monitoring'] as boolean | undefined) && (
                <div className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-700 dark:text-green-400 mt-0.5 shrink-0" />
                  <span className="text-zinc-700 dark:text-zinc-300">Real-time monitoring</span>
                </div>
              )}
              {(upgradeTier.features?.['has_api_access'] as boolean | undefined) && (
                <div className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-700 dark:text-green-400 mt-0.5 shrink-0" />
                  <span className="text-zinc-700 dark:text-zinc-300">API access</span>
                </div>
              )}
              {(upgradeTier.features?.['has_audit_logs'] as boolean | undefined) && (
                <div className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-700 dark:text-green-400 mt-0.5 shrink-0" />
                  <span className="text-zinc-700 dark:text-zinc-300">Audit logs</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Additional note */}
        <p className="text-xs text-center text-zinc-500 dark:text-zinc-500">
          You can change or cancel your plan at any time from your settings.
        </p>
      </div>
    </StandardModal>
  );
}
